import './app.scss';
import domready from 'domready';
import 'lazysizes';
import { getOffset } from './utilities/js/helper';
import { loadLazyImages } from './utilities/js/lazy-image-loader';
import { EventScroller } from './utilities/js/event-scroller';
import { ResizeHandler } from './utilities/js/resize-handler';
import { MediaMatchHandler } from './utilities/js/media-match-handler';
import { Bookmark } from './components/bookmark/bookmark';

// GLOBAL OPTIONS
window.OPTIONS = { // eslint-disable-line
    loader: '<span class="loading"><span class="loader"></span></span>',
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    },
    speed: 500
};

window.eventScroller = new EventScroller();
window.resizeHandler = new ResizeHandler();
window.mediaMatch = new MediaMatchHandler();

window.youtubeApi = false;
window.youtubeApiLoading = false;
window.anchorTimeout = null;
window.bookmark = new Bookmark();
window.accordionLoaded = false;
window.isWaitingForAnchor = false;

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
};

window.checkInView = (entries, observer) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            if (!entry.target.classList.contains('inview')) {
                entry.target.classList.add('inview');

                if (typeof entry.target.dataset.footer === 'undefined') {
                    window.inViewObserver.unobserve(entry.target);
                }
            }
        }
    });
};

window.inViewObserver = new IntersectionObserver(window.checkInView, {
    threshold: 0,
    rootMargin: '0px 100% -10% 100%'
});

const jumpToAnchor = (targetId) => {
    const $target = document.getElementById(targetId);

    if ($target !== null) {
        const anchorDimensions = getOffset($target);
        let anchorDimensionsTop = Math.floor(anchorDimensions.top);
        const anchorNavigation = document.querySelector('[data-anchor-navigation="root"]');
        const $nextElement = $target.parentElement.nextElementSibling;

        // because the anchor navigation is now a fixed element we have to reduce the scroll to position to prevent overlapping
        if (anchorNavigation) {
            anchorDimensionsTop = anchorDimensionsTop - Math.floor(anchorNavigation.offsetHeight + 28);
        }

        window.scrollTo({
            top: anchorDimensionsTop,
            left: 0,
            behavior: 'smooth'
        });

        setTimeout(() => {
            if ($nextElement) {
                $nextElement.setAttribute('tabindex', '0');
                $nextElement.focus();

                setTimeout(() => {
                    $nextElement.removeAttribute('tabindex');
                }, 400);
            }
        }, 1000);
    }
};

const initDataForCheckboxes = ($content) => {
    const $$checkboxTrigger = $content.querySelectorAll('[data-for-checkbox]');

    if ($$checkboxTrigger.length > 0) {
        const resetAllTriggersByReference = (reference, state) => {
            const $allReferenceCheckboxes = $content.querySelectorAll(`[data-for-checkbox="${reference}"]`);

            if ($allReferenceCheckboxes.length > 0) {
                $allReferenceCheckboxes.forEach($checkboxTrigger => {
                    let triggerActiveAttribute = 'aria-pressed';

                    if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                        triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
                    }

                    $checkboxTrigger.setAttribute(triggerActiveAttribute, state);
                });
            }
        };

        $$checkboxTrigger.forEach($checkboxTrigger => {
            const checkboxReference = $checkboxTrigger.dataset.forCheckbox;
            let triggerActiveAttribute = 'aria-pressed';

            if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
            }

            const $checkbox = $content.querySelector(`#${checkboxReference}`);

            $checkboxTrigger.onclick = () => {
                if ($checkbox.checked === true) {
                    $checkbox.checked = false;
                    resetAllTriggersByReference(checkboxReference, false);
                } else {
                    $checkbox.checked = true;
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                }
            };

            if ($checkbox) {
                $checkbox.onchange = () => {
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                };
            }
        });
    }
};

const initContent = ($content) => {
    const $$anchor = $content.querySelectorAll('a[href*="#"]:not([data-anchor-navigation])');
    for (let i = 0; i < $$anchor.length; i++) {
        const $anchor = $$anchor[i];

        $anchor.addEventListener('click', (e) => {
            let baseUrl = $anchor.getAttribute('href').split('#')[0];
            const targetId = $anchor.getAttribute('href').split('#')[1];
            const currentUrl = window.location.href;

            if (baseUrl === '') {
                baseUrl = currentUrl;
            }

            if (baseUrl === currentUrl) {
                jumpToAnchor(targetId);
                e.preventDefault();
            }
        });
    }

    window.addEventListener('content.loaded', (e) => {
        initDataForCheckboxes($content);

        const $$checkboxButtons = $content.querySelectorAll('input[type="checkbox"][data-checkbox-button]');
        $$checkboxButtons.forEach($toggle => {
            $toggle.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    $toggle.checked = !$toggle.checked;
                }
            });
        });

        if (e.detail.ajax && !window.isWaitingForAnchor) {
            const hash = window.location.hash;
            if (hash) {
                const targetId = hash.split('#')[1];
                let wait = 0;
                window.isWaitingForAnchor = true;

                const waitForAnchor = setInterval(() => {
                    if (document.getElementById(targetId)) {
                        clearInterval(waitForAnchor);

                        setTimeout(() => {
                            jumpToAnchor(targetId);
                        }, 400);
                    } else {
                        wait++;

                        if (wait >= 40) {
                            window.isWaitingForAnchor = false;
                            clearInterval(waitForAnchor);
                        }
                    }
                }, 300);
            }
        }
    });

    if (window.location.hash && window.siteheaderAPI) {
        const targetId = window.location.hash.split('#')[1];
        jumpToAnchor(targetId);
    }

    window.bookmark.initialize(document);

    loadLazyImages();
};

domready(function () {
    const initApplication = () => {
        browserCheck();

        initContent(document);

        window.eventScroller.init();
        window.resizeHandler.init();

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        window.dispatchEvent(contentUpdateEvent);

        window.dataInview = document.querySelectorAll('[data-inview]:not(.inview)');
        setTimeout(() => {
            window.dataInview.forEach((dataInView) => {
                window.inViewObserver.observe(dataInView);
            });
        }, 500);
    };

    initApplication();
});
